import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import { 
  FaWhatsapp, 
  FaEnvelope, 
  FaInstagram 
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AnimatedSection from '../Animation/AnimatedSection';

const ContactSection = styled.section`
  padding: 6rem 0;
  background-color: ${props => props.theme.colors.white};
`;

const SectionTitle = styled(motion.h2)`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
  margin-bottom: 3rem;
  text-transform: uppercase;
  font-style: italic;
  
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.grayMedium};
  }
`;

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  width: 100%;
  max-width: 100%;
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ContactInfo = styled(motion.div)`
  @media screen and (max-width: 768px) {
    text-align: center;
  }
`;

const ContactText = styled(motion.p)`
  margin-bottom: 2rem;
  text-align: justify;
  text-justify: inter-word;
`;

const ContactChannels = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ContactChannel = styled(motion.a)`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.grayMedium};
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateX(5px);
  }
  
  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`;

const ChannelIcon = styled(motion.div)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.grayLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
  transition: all 0.3s ease;
`;

const ContactForm = styled(motion.form)`
  background-color: ${props => props.theme.colors.grayLight};
  padding: 2rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.boxShadow};
`;

const FormGroup = styled(motion.div)`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const Input = styled(motion.input)`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.grayMedium};
  border-radius: ${props => props.theme.borderRadius.small};
  font-family: ${props => props.theme.fonts.body};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(27, 43, 77, 0.2);
  }
`;

const TextArea = styled(motion.textarea)`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.grayMedium};
  border-radius: ${props => props.theme.borderRadius.small};
  font-family: ${props => props.theme.fonts.body};
  resize: vertical;
  min-height: 150px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(27, 43, 77, 0.2);
  }
`;

const Contact = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const [formRef, formInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappNumber = '5519998621928';

    const whatsappMessage = `Olá! Vim através do site e gostaria de solicitar um orçamento.\n\n` +
      `*Dados do Cliente:*\n` +
      `• Nome: ${formData.name}\n` +
      `• Empresa: ${formData.company}\n` +
      `• E-mail: ${formData.email}\n` +
      `*Mensagem:*\n${formData.message}\n\n` +
      `Aguardo retorno. Obrigado(a)!`;

    const encoded = encodeURIComponent(whatsappMessage);
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encoded}`;
    window.open(whatsappURL, '_blank');

    setFormData({ name: '', company: '', email: '', phone: '', message: '' });
  };

  const channelVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.3 + (i * 0.1),
        duration: 0.5,
        ease: "easeOut"
      }
    }),
  };
  
  const formItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.3 + (i * 0.1),
        duration: 0.5,
        ease: "easeOut"
      }
    }),
  };
  
  const channels = [
    {
      icon: <FaWhatsapp />,
      link: "https://wa.me/5519998621928",
      text: "WhatsApp: (19) 98621-9280",
      external: true
    },
    {
      icon: <FaEnvelope />,
      link: "mailto:lmcertsolutions@outlook.com",
      text: "lmcertsolutions@outlook.com",
      external: false
    },
    {
      icon: <FaInstagram />,
      link: "http://instagram.com/lmconsulting.global",
      text: "@lmconsulting.global",
      external: true
    }
  ];
  
  return (
    <ContactSection id="contato">
      <div className="container">
        <SectionTitle
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          Entre em Contato
        </SectionTitle>
        
        <ContactContainer>
          <AnimatedSection animation="fadeInLeft" delay={0.2}>
            <ContactInfo>
              <ContactText>
                Estamos prontos para apoiar sua empresa em todas as etapas dos processos de certificação e conformidade regulatória. Entre em contato para conhecer nossos serviços ou solicitar uma consultoria personalizada, alinhada às necessidades e desafios do seu produto e do seu mercado.
              </ContactText>
              
              <ContactChannels>
                {channels.map((channel, index) => (
                  <ContactChannel 
                    key={index}
                    href={channel.link}
                    target={channel.external ? "_blank" : undefined}
                    rel={channel.external ? "noopener noreferrer" : undefined}
                    custom={index}
                    variants={channelVariants}
                    initial="hidden"
                    animate={titleInView ? "visible" : "hidden"}
                    whileHover={{ x: 5 }}
                  >
                    <ChannelIcon whileHover={{ scale: 1.1, rotate: 5 }}>
                      {channel.icon}
                    </ChannelIcon>
                    <span>{channel.text}</span>
                  </ContactChannel>
                ))}
              </ContactChannels>
            </ContactInfo>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeInRight" delay={0.4}>
            <ContactForm
              ref={formRef}
              initial={{ opacity: 0, y: 30 }}
              animate={formInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.7 }}
              onSubmit={handleSubmit}
            >
              <FormGroup
                custom={0}
                variants={formItemVariants}
                initial="hidden"
                animate={formInView ? "visible" : "hidden"}
              >
                <Label htmlFor="name">Nome</Label>
                <Input 
                  type="text" 
                  id="name" 
                  name="name" 
                  placeholder="Seu nome"
                  value={formData.name}
                  onChange={handleChange}
                  whileFocus={{ scale: 1.01 }}
                />
              </FormGroup>
              
              <FormGroup
                custom={1}
                variants={formItemVariants}
                initial="hidden"
                animate={formInView ? "visible" : "hidden"}
              >
                <Label htmlFor="company">Empresa</Label>
                <Input 
                  type="text" 
                  id="company" 
                  name="company" 
                  placeholder="Nome da empresa"
                  value={formData.company}
                  onChange={handleChange}
                  whileFocus={{ scale: 1.01 }}
                />
              </FormGroup>
              
              <FormGroup
                custom={2}
                variants={formItemVariants}
                initial="hidden"
                animate={formInView ? "visible" : "hidden"}
              >
                <Label htmlFor="email">E-mail</Label>
                <Input 
                  type="email" 
                  id="email" 
                  name="email" 
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  whileFocus={{ scale: 1.01 }}
                />
              </FormGroup>
              
              <FormGroup
                custom={3}
                variants={formItemVariants}
                initial="hidden"
                animate={formInView ? "visible" : "hidden"}
              >
                <Label htmlFor="message">Mensagem</Label>
                <TextArea 
                  id="message" 
                  name="message" 
                  placeholder="Como podemos ajudar?"
                  value={formData.message}
                  onChange={handleChange}
                  whileFocus={{ scale: 1.01 }}
                />
              </FormGroup>
              
              <motion.div
                custom={4}
                variants={formItemVariants}
                initial="hidden"
                animate={formInView ? "visible" : "hidden"}
              >
                <Button 
                  tertiary 
                  fullWidth
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FaWhatsapp style={{ marginRight: '8px' }} />
                  Enviar via WhatsApp
                </Button>
              </motion.div>
            </ContactForm>
          </AnimatedSection>
        </ContactContainer>
      </div>
    </ContactSection>
  );
};

export default Contact;
